import { IAPIResponseError, IError } from '@checkout/errors';
import { ICountryChangeRequestPayload, ICountryChangeSuccessPayload, ICountryStateChangePayload } from '@checkout/payloadTypes';
import { RootState } from '@checkout/state';
import { AxiosResponse } from 'axios';
import { Epic, ofType } from 'redux-observable';
import { fromPromise as fromPromise$ } from 'rxjs/observable/fromPromise';
import { of as of$ } from 'rxjs/observable/of';
import { catchError, filter, flatMap, mergeMap, startWith } from 'rxjs/operators';
import { checkoutApi } from 'ys-analytics';
import { CountryStateChangeRequestAction, RootAction } from '../actions/actionTypes.d';
import { getStatesItems } from '../utils/address';
import convertMessages from '../utils/convertErrors';
import { FormNames } from '../utils/formNames';
import { ingridResume, ingridSuspend } from '../utils/widgets';
import ActionTypes from './../actions/actionTypes';
import { CountryChangeRequestAction } from './../actions/actionTypes.d';
import {
  countryChangeRequest,
  countryChangeSuccess,
  countryStateChangeRequest,
  countryStateChangeSuccess,
  fetchKCOWidgetRequest,
  responseError,
  setErrors } from './../reducers/checkout';
import { rest } from './../utils/apiUtils';
import { logger } from './../utils/logger';
import { isKlarnaCheckout } from './../utils/paymentMethods';

const { eeCheckoutSteps } = checkoutApi;

export const changeCountryEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.COUNTRY_CHANGE_REQUEST),
    mergeMap((action: CountryChangeRequestAction) => {
      ingridSuspend();
      return fromPromise$(rest.httpPut(`/countries/${action.payload.countryCode}`))
        .pipe(
          flatMap((response: AxiosResponse<ICountryChangeSuccessPayload>) => {
            const payload = Object.assign(response.data, { formName: action.payload.formName });
            const { paymentMethod } = response.data.selection;

            ingridResume();
            try {
              eeCheckoutSteps.countryChange(response.data.location.country, response.data.selection.currency);
            } catch (err) {
              // tslint:disable-next-line:no-console
              console.error('Enhanced Ecommerce error: ', err);
            }
            if (isKlarnaCheckout(paymentMethod)) {
              return of$(fetchKCOWidgetRequest()).pipe(startWith(countryChangeSuccess(payload)));
            }
            return of$(countryChangeSuccess(payload));
          }),
          catchError((error: IError) => {
            logger(error);
            return of$(setErrors(error));
          })
        );
    })
  );

export const changeCountryStateEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.COUNTRY_STATE_CHANGE_REQUEST),
    mergeMap((action: CountryStateChangeRequestAction) => {
      ingridSuspend();
      return fromPromise$(rest.httpPut(`/countries/${action.payload.countryCode}/states/${action.payload.stateCode}`))
        .pipe(
          flatMap((response: AxiosResponse<ICountryChangeSuccessPayload>) => {
            const payload = Object.assign(response.data, { formName: action.payload.formName });

            return of$(countryStateChangeSuccess(payload));
          }),
          catchError((error: IError) => {
            logger(error);
            if (error.response && error.response.data && error.response.data.selection) {
              const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
              const responseApiErrorPayload: IAPIResponseError = {
                errors: convertedMessages,
                selection: error.response.data.selection,
              };
              return of$(responseError(responseApiErrorPayload));
            }
            return of$(setErrors(error));
          })
        );
    })
  );

/*
  Fired countryChangeRequest or countryStateChangeRequest when PRESELECT_INITIAL_VALUES action accurs and
  "Remember me" checkbox is checked, so we have to let the API know that user has preselected/remembered country or state
*/
export const preselectRememberedStateAndCountryEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.PRESELECT_INITIAL_VALUES),
    filter(() => state$.value.checkout.fieldsData.rememberMe !== undefined && state$.value.checkout.fieldsData.rememberMe &&
    (state$.value.checkout.location.country !== state$.value.checkout.fieldsData.shippingAddress.country)),
    mergeMap(() => {
      if (getStatesItems(state$.value.checkout.countries, state$.value.checkout.fieldsData.shippingAddress.country)) {
        const stateChangePayload: ICountryStateChangePayload = {
          countryCode: state$.value.checkout.fieldsData.shippingAddress.country,
          stateCode: state$.value.checkout.fieldsData.shippingAddress.state,
          formName: FormNames.shippingAddress
        };
        return  of$(countryStateChangeRequest(stateChangePayload));
      }
      const countryChangePayload: ICountryChangeRequestPayload = {
        countryCode: state$.value.checkout.fieldsData.shippingAddress.country,
        formName: FormNames.shippingAddress
      };
      return  of$(countryChangeRequest(countryChangePayload));
    })
);
